import type { ComponentType } from 'react'
import { useMemo } from 'react'
import { useLooseProductContextSelector } from 'src/components/restructure/product/contexts'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { getInitialItem } from 'src/components/restructure/utils/get-initial-sku'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { PictureSelector } from './PictureSelector'
import { DefaultSelector } from './DefaultSelector'
import type { Variation } from './types'
import { DropdownSelector } from './DropdownSelector/DropdownSelector'
import { GenderSwitch } from '../GenderSwitch'
import type { Product } from '../../../types/product'
import type { ProductItem } from '../../../types/productItem'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COMPONENTS: Record<string, ComponentType<any>> = {
  Cor: PictureSelector,
  Sabor: DefaultSelector,
  Tamanho: DefaultSelector,
  Valor: DefaultSelector,
  Voltagem: DefaultSelector,
}

interface Props {
  variations: Variation[]
  onClickVariation: (
    name: any,
    value: string,
    manufacturerCode?: string
  ) => void
  isFromStickyBar?: boolean
  isFirstFoldRevampEnabled?: boolean
  hasDisabledButtons?: boolean
  productFromProps?: Product
  currentSkuFromProps?: ProductItem
}

export function RenderedSelectors({
  variations,
  onClickVariation,
  isFromStickyBar,
  isFirstFoldRevampEnabled,
  hasDisabledButtons,
  productFromProps,
  currentSkuFromProps,
}: Readonly<Props>) {
  const contextProduct = useLooseProductContextSelector((ctx) => ctx.product)
  const contextCurrentSku = useLooseProductContextSelector(
    (ctx) => ctx.currentSku
  )

  const { pageData } = useGlobalContext()
  const product = productFromProps ?? contextProduct
  const currentSku = currentSkuFromProps ?? contextCurrentSku

  const standardObject = useMemo(() => getStandardObject(pageData), [pageData])
  const productObject = useMemo(
    () => getProductQueryObject(getInitialItem(product.items, product.slug)),
    [product]
  )

  const colorIndex = variations.findIndex(
    (variation) => variation.field.name === 'Cor'
  )

  const sizeIndex = variations.findIndex(
    (variation) => variation.field.name === 'Tamanho'
  )

  const shouldInsertBetweenColorAndSize =
    colorIndex !== -1 && sizeIndex !== -1 && colorIndex < sizeIndex

  const genderSwitchInsertionIndex = shouldInsertBetweenColorAndSize
    ? sizeIndex
    : 0

  return (
    <>
      {isFromStickyBar
        ? variations.map((variation) => {
            const props = {
              variation,
              onClickVariation,
              standardObject,
              productObject,
            }

            return (
              <DropdownSelector
                key={variation.field.id}
                {...props}
                hasDisabledButtons={hasDisabledButtons}
                currentSkuFromProps={currentSku}
                productFromProps={product}
              />
            )
          })
        : (() => {
            const selectorNodes = variations.map((variation) => {
              const variationName = variation.field.name
              const Component = COMPONENTS[variationName]

              const props = {
                variation,
                onClickVariation,
                standardObject,
                productObject,
              }

              if (!Component) {
                return (
                  <DefaultSelector
                    key={variation.field.id}
                    currentSkuFromProps={currentSku}
                    productFromProps={product}
                    isFirstFoldRevampEnabled={isFirstFoldRevampEnabled}
                    {...props}
                  />
                )
              }

              return (
                <Component
                  key={variation.field.id}
                  currentSkuFromProps={currentSku}
                  productFromProps={product}
                  isFirstFoldRevampEnabled={isFirstFoldRevampEnabled}
                  {...props}
                />
              )
            })

            selectorNodes.splice(
              genderSwitchInsertionIndex,
              0,
              <GenderSwitch key="gender-switch" />
            )

            return selectorNodes
          })()}
    </>
  )
}
